import React, { useState } from 'react';

interface Task {
  id: string;
  title: string;
  quadrant: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  status: 'Not Started' | 'In Progress' | 'Done';
  area: string;
  dueDate?: string;
}

function Tasks() {
  const [filter, setFilter] = useState<string>('all');

  // TODO: Fetch from Notion API
  const tasks: Task[] = [
    { id: '1', title: 'Update Notion API integration', quadrant: 'Q1', status: 'Done', area: 'Development' },
    { id: '2', title: 'Build Mission Control dashboard', quadrant: 'Q1', status: 'In Progress', area: 'Development' },
    { id: '3', title: 'Set up CI/CD pipeline', quadrant: 'Q2', status: 'Not Started', area: 'DevOps' },
    { id: '4', title: 'Write documentation', quadrant: 'Q2', status: 'Not Started', area: 'Documentation' },
    { id: '5', title: 'Review PR from Architect', quadrant: 'Q3', status: 'Not Started', area: 'Development' },
  ];

  const quadrantLabels = {
    Q1: { label: 'Urgent & Important', color: 'bg-mc-danger' },
    Q2: { label: 'Important', color: 'bg-mc-accent' },
    Q3: { label: 'Urgent', color: 'bg-mc-warning' },
    Q4: { label: 'Neither', color: 'bg-mc-muted' },
  };

  const statusColors = {
    'Not Started': 'text-mc-muted',
    'In Progress': 'text-mc-warning',
    'Done': 'text-mc-success',
  };

  const filteredTasks = filter === 'all'
    ? tasks
    : tasks.filter(t => t.quadrant === filter);

  return (
    <div>
      <header className="mb-8">
        <h1 className="text-3xl font-bold">Tasks</h1>
        <p className="text-mc-muted mt-2">Manage your Eisenhower Matrix tasks from Notion</p>
      </header>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
          <button
            key={q}
            onClick={() => setFilter(q)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === q
                ? 'bg-mc-accent text-white'
                : 'bg-mc-card border border-mc-border text-mc-muted hover:text-mc-text'
            }`}
          >
            {q === 'all' ? 'All Tasks' : `${q}: ${quadrantLabels[q as keyof typeof quadrantLabels].label}`}
          </button>
        ))}
      </div>

      {/* Eisenhower Matrix Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {(['Q1', 'Q2', 'Q3', 'Q4'] as const).map((q) => (
          <div key={q} className="card">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-3 h-3 rounded ${quadrantLabels[q].color}`}></span>
              <h3 className="font-semibold">{q}: {quadrantLabels[q].label}</h3>
              <span className="text-mc-muted text-sm ml-auto">
                {tasks.filter(t => t.quadrant === q).length} tasks
              </span>
            </div>

            <div className="space-y-3">
              {tasks
                .filter(t => t.quadrant === q)
                .map(task => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 bg-mc-bg rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{task.title}</p>
                      <p className="text-xs text-mc-muted">{task.area}</p>
                    </div>
                    <span className={`text-sm ${statusColors[task.status]}`}>
                      {task.status}
                    </span>
                  </div>
                ))}
              {tasks.filter(t => t.quadrant === q).length === 0 && (
                <p className="text-mc-muted text-sm text-center py-4">No tasks</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Task List */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">
          {filter === 'all' ? 'All Tasks' : `${filter} Tasks`}
          <span className="text-mc-muted font-normal ml-2">({filteredTasks.length})</span>
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-mc-muted text-sm border-b border-mc-border">
                <th className="pb-3">Task</th>
                <th className="pb-3">Quadrant</th>
                <th className="pb-3">Area</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => (
                <tr key={task.id} className="border-b border-mc-border last:border-0">
                  <td className="py-3">{task.title}</td>
                  <td className="py-3">
                    <span className={`inline-flex items-center gap-2`}>
                      <span className={`w-2 h-2 rounded ${quadrantLabels[task.quadrant].color}`}></span>
                      {task.quadrant}
                    </span>
                  </td>
                  <td className="py-3 text-mc-muted">{task.area}</td>
                  <td className={`py-3 ${statusColors[task.status]}`}>{task.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Tasks;
